import React, { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { apiRequest } from "../utils/api";
import AvatarImg from "../components/AvatarImg";

const AvatarPicker = ({ avatars, selectedUrl, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="w-14 h-14 rounded-full bg-game-dark/60 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
      {avatars.map((avatar) => {
        const isSelected = selectedUrl === avatar.url;
        return (
          <button
            key={avatar.id}
            type="button"
            onClick={() => onSelect(avatar.url)}
            title={avatar.id}
            className={`w-14 h-14 rounded-full border-2 flex items-center justify-center overflow-hidden transition-all duration-200 hover:scale-110 focus:outline-none ${
              isSelected
                ? "border-game-neon shadow-[0_0_14px_rgba(255,184,0,0.6)] scale-110"
                : "border-game-border hover:border-game-muted/60 bg-game-dark"
            }`}
          >
            <AvatarImg src={avatar.url} alt={avatar.id} className="w-9 h-9 object-contain" />
          </button>
        );
      })}
    </div>
  );
};

// ── Account Settings Tab ──────────────────────────────────────────────────────
const AccountSettings = () => {
  const [profileLoading, setProfileLoading] = useState(true);

  const [avatars, setAvatars] = useState([]);
  const [avatarsLoading, setAvatarsLoading] = useState(true);

  // Form state
  const [gamerName, setGamerName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── Fetch profile ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setProfileLoading(true);
        const res = await apiRequest("/players/me");
        if (!res.ok) throw new Error();
        const data = await res.json();
        setGamerName(data.gamerName || "");
        setBio(data.bio || "");
        // Store the raw avatarUrl — validity is checked after avatars load
        setAvatarUrl(data.avatarUrl || "");
      } catch {
        // silently fail — form will just be empty
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // ── Fetch avatars ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        setAvatarsLoading(true);
        const res = await apiRequest("/Auth/avatars");
        if (!res.ok) throw new Error();
        const data = await res.json();
        setAvatars(data);
        // If the stored avatarUrl isn't one of the real avatars, clear it
        // so no broken placeholder is shown as "selected"
        setAvatarUrl((prev) => {
          const validUrls = data.map((a) => a.url);
          return validUrls.includes(prev) ? prev : "";
        });
      } catch {
        setAvatars([]);
      } finally {
        setAvatarsLoading(false);
      }
    };
    fetchAvatars();
  }, []);

  // ── Save handler ────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const res = await apiRequest("/Players/me", {
        method: "PUT",
        body: JSON.stringify({ gamerName, bio, avatarUrl }),
      });

      if (res.status === 204 || res.ok) {
        setSaveSuccess(true);
        window.dispatchEvent(new CustomEvent("avatar-update", { detail: avatarUrl }));
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const err = await res.json().catch(() => ({}));
        setSaveError(err?.message || "Failed to save. Please try again.");
      }
    } catch {
      setSaveError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full bg-game-dark/50 border border-game-border rounded-xl p-4 text-white outline-none focus:border-game-neon focus:shadow-[0_0_15px_rgba(255,184,0,0.2)] transition-all placeholder:text-game-muted/50";

  if (profileLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-56 bg-game-dark/60 rounded" />
        <div className="h-14 bg-game-dark/60 rounded-xl" />
        <div className="h-14 bg-game-dark/60 rounded-xl" />
        <div className="h-28 bg-game-dark/60 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <h2 className="text-3xl font-heading font-black italic uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-game-neon to-game-accent drop-shadow-[0_0_10px_rgba(255,184,0,0.3)]">
        Account Settings
      </h2>

      {/* Avatar Picker */}
      <div className="space-y-3">
        <label className="text-xs font-bold uppercase tracking-widest text-game-muted ml-1 block">
          Choose Avatar
        </label>
        {/* Current avatar preview */}
        {avatarUrl && (
          <div className="flex items-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-full border-2 border-game-neon shadow-[0_0_14px_rgba(255,184,0,0.4)] bg-game-dark flex items-center justify-center overflow-hidden">
              <AvatarImg src={avatarUrl} alt="Current avatar" className="w-10 h-10 object-contain" />
            </div>
            <span className="text-xs text-game-muted uppercase tracking-widest font-bold">Current</span>
          </div>
        )}
        <AvatarPicker
          avatars={avatars}
          selectedUrl={avatarUrl}
          onSelect={setAvatarUrl}
          loading={avatarsLoading}
        />
      </div>

      {/* Gamer Name */}
      <div className="space-y-2">
        <label className="text-xs font-bold uppercase tracking-widest text-game-muted ml-1 block">
          Gamer Name
        </label>
        <input
          type="text"
          value={gamerName}
          onChange={(e) => setGamerName(e.target.value)}
          placeholder="Enter your gamer name"
          className={inputClass}
        />
      </div>

      {/* Bio — Fix #7: maxLength prevents oversized payloads */}
      <div className="space-y-2">
        <label className="text-xs font-bold uppercase tracking-widest text-game-muted ml-1 block">
          Bio
        </label>
        <textarea
          rows={4}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Tell the world about yourself..."
          maxLength={300}
          className={`${inputClass} resize-none`}
        />
        <p className={`text-right text-[10px] font-bold uppercase tracking-widest ${
          bio.length >= 280 ? 'text-rose-400' : 'text-game-muted'
        }`}>
          {bio.length} / 300
        </p>
      </div>

      {/* Error message */}
      {saveError && (
        <p className="text-xs font-bold text-red-400 uppercase tracking-widest">{saveError}</p>
      )}

      {/* Save Button */}
      <div className="pt-4 border-t border-game-border flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="game-btn px-10 py-4 font-black uppercase tracking-widest rounded-xl text-sm shadow-[0_0_20px_rgba(255,94,14,0.4)] relative overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </>
          ) : saveSuccess ? (
            <>
              <Check size={16} className="text-green-400" />
              <span className="text-green-400">Saved!</span>
            </>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </div>
  );
};

// ── Main Settings Page ────────────────────────────────────────────────────────
const Settings = () => {
  const [activeTab, setActiveTab] = useState("Account");
  const tabs = ["Account"];

  const renderTabContent = () => {
    switch (activeTab) {
      case "Account":
        return <AccountSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-8 p-6 max-w-6xl mx-auto animate-in fade-in duration-500">
      {/* Sidebar Navigation */}
      <nav className="w-full md:w-64 space-y-2 shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`w-full text-left p-4 text-sm font-bold tracking-widest uppercase transition-all duration-300 rounded-xl flex items-center gap-3 ${
              activeTab === tab
                ? "bg-gradient-to-r from-game-accent/20 to-game-neon/20 border border-game-neon/30 text-white shadow-[0_0_15px_rgba(255,184,0,0.2)] translate-x-2"
                : "text-game-muted border border-transparent hover:bg-white/5 hover:text-white hover:translate-x-1"
            }`}
          >
            {tab}
          </button>
        ))}
      </nav>

      {/* Dynamic Content Area */}
      <div className="flex-1 max-w-3xl glass-panel p-8 md:p-12 rounded-3xl relative overflow-hidden shadow-2xl shadow-game-neon/10">
        <div className="min-h-[400px]">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default Settings;